import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  ARTICLE_DRAFTS_KEY,
  newDraftId,
  readArticleDrafts,
  writeArticleDrafts,
  type ArticleDraft,
  type ArticleDraftStore,
} from '@/lib/articleDrafts';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/**
 * Module-level cache + subscriber list, same pattern as
 * `useOfflineLibrary`: every mounted consumer (the editor's save
 * button, the drafts list, the Write menu's draft count) must stay
 * consistent the moment a draft is saved or deleted, without waiting
 * for a remount.
 */
let cache: ArticleDraftStore | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): ArticleDraftStore {
  // Must be referentially stable between changes, or
  // useSyncExternalStore re-renders forever.
  cache ??= readArticleDrafts();
  return cache;
}

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== null && e.key !== ARTICLE_DRAFTS_KEY) return;
    cache = null;
    emit();
  });
}

function mutate(update: (store: ArticleDraftStore) => ArticleDraftStore): void {
  const next = update(getSnapshot());
  // Write first: if storage is full this throws, and the in-memory
  // cache must not claim a save that didn't actually persist.
  writeArticleDrafts(next);
  cache = next;
  emit();
}

export interface DraftInput {
  title: string;
  summary: string;
  content: string;
  slug: string;
}

export interface UseArticleDraftsResult {
  /** The current user's drafts, most recently updated first. */
  drafts: ArticleDraft[];
  count: number;
  /** One draft by id, or undefined — the editor uses this to resume. */
  get: (id: string | undefined) => ArticleDraft | undefined;
  /**
   * Creates or updates a draft and returns its id. If `existingId` is
   * provided and still belongs to the current user, that draft is
   * updated in place; otherwise a new draft is created.
   */
  save: (input: DraftInput, existingId?: string) => string;
  /** Inserts a synced draft preserving its timestamps — for sync only. */
  putSynced: (draft: ArticleDraft) => void;
  remove: (id: string) => void;
  clear: () => void;
}

/**
 * Local, unpublished article drafts — works in progress kept only on
 * this device, scoped to the currently logged-in Nostr identity.
 *
 * Drafts are deliberately *not* the same thing as the offline library:
 * an offline-saved article is a complete, already-published, signed
 * event kept for reading; a draft is unfinished, unsigned text being
 * written, and is never publishable as-is or shown to other identities
 * on the same device.
 */
export function useArticleDrafts(): UseArticleDraftsResult {
  const store = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const { user } = useCurrentUser();
  const pubkey = user?.pubkey;

  const drafts = useMemo(
    () =>
      Object.values(store)
        .filter((draft) => !pubkey || draft.pubkey === pubkey)
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [store, pubkey],
  );

  const get = useCallback(
    (id: string | undefined) => {
      if (!id) return undefined;
      const draft = store[id];
      // Never hand back another identity's draft to the current session.
      if (!draft || (pubkey && draft.pubkey !== pubkey)) return undefined;
      return draft;
    },
    [store, pubkey],
  );

  const save = useCallback(
    (input: DraftInput, existingId?: string): string => {
      if (!pubkey) throw new Error('Must be logged in to save a draft.');

      const existing = existingId ? store[existingId] : undefined;
      const id = existing && existing.pubkey === pubkey ? existing.id : newDraftId();
      const now = Date.now();

      mutate((current) => ({
        ...current,
        [id]: {
          id,
          pubkey,
          title: input.title,
          summary: input.summary,
          content: input.content,
          slug: input.slug,
          createdAt: existing?.createdAt ?? now,
          updatedAt: now,
        },
      }));
      return id;
    },
    [store, pubkey],
  );

  /**
   * Inserts or replaces a draft, preserving its exact timestamps and
   * pubkey — used by sync, which must never "touch" a draft (that would
   * make it look newer than what's on relays and trigger a re-sync loop)
   * or reassign it to the current identity (a synced draft already
   * carries its author's pubkey).
   */
  const putSynced = useCallback((draft: ArticleDraft) => {
    mutate((current) => ({
      ...current,
      [draft.id]: draft,
    }));
  }, []);

  const remove = useCallback((id: string) => {
    mutate((current) => {
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    if (!pubkey) return;
    // Only clear the current identity's drafts, not everyone's on this device.
    mutate((current) => {
      const next: ArticleDraftStore = {};
      for (const [key, draft] of Object.entries(current)) {
        if (draft.pubkey !== pubkey) next[key] = draft;
      }
      return next;
    });
  }, [pubkey]);

  return { drafts, count: drafts.length, get, save, putSynced, remove, clear };
}
